import { Button, LinkButton, MuiAlert, ResponsiveDrawer, Spin, Typography, useRenderer } from '@fox/atoms';
import { PaymentMethod } from '@fox/finance';
import { FlexColumnSpaced, FlexRowSpaced } from '@fox/layout';
import { useSubscribeTopicWithHandler } from 'fox-extensions';
import { useState } from 'react';
import { useTheme } from 'styled-components';
import { useContentContext } from '../../../content-context';
import { useFinancialContext } from '../../../financial-config-context';
import { FINANCIAL_DATA_FETCHED, REFETCH_FINANCIAL } from '../../../topics';
import { useDeletePaymentMethodMutation } from '../../mutation-hooks/mutation-implementations';
import { SubscriptionSelector } from './subscription-selector';

export function MobileOptions({
	id,
	paymentMethod,
	update,
	updateError,
	updateLoading,
	useLinkedOrganisationData,
	allowedPlanCategories
}: {
	id: string | number;
	paymentMethod: PaymentMethod;
	updateLoading: boolean;
	updateError: string;
	update: (selectedSubscriptionIds: string[]) => Promise<void>;
	useLinkedOrganisationData: boolean;
	allowedPlanCategories?: string[];
}) {
	const { getSubscriptions, getCreditCard, getDirectDebit } = useFinancialContext();
	const subscriptions = getSubscriptions(useLinkedOrganisationData, allowedPlanCategories);
	const method =
		paymentMethod === PaymentMethod.CreditCard
			? getCreditCard(id as string, useLinkedOrganisationData)
			: getDirectDebit(id as string, useLinkedOrganisationData);
	const { inUse } = method;

	const render = useRenderer();
	useSubscribeTopicWithHandler(FINANCIAL_DATA_FETCHED, () => {
		const subs = getSubscriptions(useLinkedOrganisationData, allowedPlanCategories);
		const newSubIds = subs
			.filter(s => (paymentMethod === PaymentMethod.CreditCard ? s.creditCardId === id : s.directDebitId === id))
			.map(s => s.id);
		setSelectedSubscriptionIds(newSubIds);
		setInitialSubscriptionIds(newSubIds);
		render();
	});

	const subscriptionIdsUsingThisPaymentMethod =
		subscriptions
			?.filter(s => (paymentMethod === PaymentMethod.CreditCard ? s.creditCardId === id : s.directDebitId === id))
			?.map(s => s.id) ?? [];

	const { requestToken, contentId } = useContentContext();
	const theme = useTheme();
	const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
	const [subscriptionSelectorDrawerOpen, setSubscriptionSelectorDrawerOpen] = useState<boolean>(false);
	const [initialSelectedSubscriptionIds, setInitialSubscriptionIds] = useState<string[]>(subscriptionIdsUsingThisPaymentMethod);
	const [selectedSubscriptionIds, setSelectedSubscriptionIds] = useState<string[]>(subscriptionIdsUsingThisPaymentMethod);
	const { error, loading, mutate } = useDeletePaymentMethodMutation({
		requestToken,
		paymentMethod,
		useLinkedOrganisationData,
		topics: [REFETCH_FINANCIAL]
	});

	return (
		<>
			<FlexRowSpaced>
				{!inUse ? (
					<LinkButton onClick={() => setDrawerOpen(true)} color={theme.palette.error.main}>
						Delete
					</LinkButton>
				) : null}
				{subscriptions?.length > 1 ? <LinkButton onClick={() => setSubscriptionSelectorDrawerOpen(true)}>Edit</LinkButton> : null}
			</FlexRowSpaced>
			<ResponsiveDrawer
				placement="bottom"
				title={'Delete payment method'}
				width="100%"
				height="100%"
				style={{ height: '100%' }}
				open={drawerOpen}
			>
				<FlexColumnSpaced style={{ padding: '1rem' }}>
					<Typography>Are you sure you would like to remove this payment method?</Typography>
					<FlexRowSpaced>
						<Button
							disabled={loading}
							variant="tertiary"
							onClick={() => {
								setDrawerOpen(false);
							}}
						>
							Cancel
						</Button>
						<Button
							disabled={loading}
							onClick={async () => {
								const res = await mutate({
									contentId,
									id: id?.toString()
								});
								if (res === true) {
									setDrawerOpen(false);
								}
							}}
							variant="primary"
						>
							Confirm
						</Button>
					</FlexRowSpaced>
					{error ? <MuiAlert severity="error">{error}</MuiAlert> : null}
				</FlexColumnSpaced>
			</ResponsiveDrawer>
			<ResponsiveDrawer
				placement="bottom"
				title={'Select subscription'}
				width="100%"
				height="100%"
				style={{ height: '100%' }}
				open={subscriptionSelectorDrawerOpen}
			>
				<FlexColumnSpaced style={{ padding: '1rem' }}>
					<Spin spinning={updateLoading}>
						<SubscriptionSelector
							selectedSubscriptionIds={selectedSubscriptionIds}
							setSelectedSubscriptionIds={setSelectedSubscriptionIds}
							subscriptions={subscriptions}
							paymentMethodId={id as string}
						/>
						<FlexRowSpaced>
							<Button
								disabled={updateLoading}
								variant="tertiary"
								onClick={() => {
									setSubscriptionSelectorDrawerOpen(false);
								}}
							>
								Back
							</Button>
							<Button
								disabled={
									updateLoading ||
									(initialSelectedSubscriptionIds.every(id => selectedSubscriptionIds.includes(id)) &&
										selectedSubscriptionIds.every(id => initialSelectedSubscriptionIds.includes(id)))
								}
								onClick={() => {
									update(selectedSubscriptionIds);
								}}
								variant="primary"
							>
								Confirm
							</Button>
						</FlexRowSpaced>
					</Spin>
					{updateError ? <MuiAlert severity="error">{updateError}</MuiAlert> : null}
				</FlexColumnSpaced>
			</ResponsiveDrawer>
		</>
	);
}
